import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AuthLayout from './auth/AuthLayout';
import AuthForm from './auth/AuthForm';
import OAuthButton from './OAuthButton';
import { API_BASE_URL } from '../App';
import { validateEmail, validatePassword } from '../lib/emailValidation';
import { storeReferralCode } from '../lib/referralStorage';
import './LoginPage.css';

function SignupPage({ product }) {
  const isCostco = product === 'costco';
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref');
  const redirect = searchParams.get('redirect') || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inviterName, setInviterName] = useState(null);

  useEffect(() => {
    if (ref) storeReferralCode();
  }, [ref]);

  useEffect(() => {
    if (!ref) return;
    fetch(`${API_BASE_URL}/invites/preview?ref=${encodeURIComponent(ref)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.inviterName) setInviterName(data.inviterName);
      })
      .catch(() => {});
  }, [ref]);

  const handleSubmit = async () => {
    setError('');
    setStatus(null);

    const emailCheck = validateEmail(email);
    const pwCheck = validatePassword(password, { confirm: confirmPassword });
    const errors = {};
    if (!emailCheck.ok) errors.email = emailCheck.error;
    if (!pwCheck.ok) errors.password = pwCheck.error;
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/sessions/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailCheck.normalized,
          password,
          ref: ref || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Registration failed');
        return;
      }
      setStatus({
        type: 'ok',
        message: data.message || 'Check your email to verify your account before signing in.',
      });
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      brand={isCostco ? 'costco' : undefined}
      title={isCostco ? 'Create account' : 'Create your account'}
      subtitle={
        isCostco
          ? 'Create an account to start your lifetime warehouse log'
          : 'Join Plate Chase and track license plates across the country'
      }
      footer={
        <p>
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      }
    >
      {inviterName && (
        <div className="auth-invite-banner" role="status">
          Invited by <strong>{inviterName}</strong>
        </div>
      )}

      <div className="login-buttons login-buttons--compact">
        <div className="login-button-group">
          <OAuthButton onBeforeOAuth={storeReferralCode} mode="signup" useApiRedirect={isCostco} />
        </div>
      </div>

      <div className="login-divider">
        <span>or sign up with email</span>
      </div>

      <AuthForm
        fields={[
          {
            id: 'signup-email',
            name: 'email',
            label: 'Email',
            type: 'email',
            value: email,
            onChange: setEmail,
            autoComplete: 'email',
            error: fieldErrors.email,
            disabled: loading || !!status,
          },
          {
            id: 'signup-password',
            name: 'password',
            label: 'Password',
            type: 'password',
            value: password,
            onChange: setPassword,
            autoComplete: 'new-password',
            error: fieldErrors.password,
            disabled: loading || !!status,
          },
          {
            id: 'signup-confirm',
            name: 'confirmPassword',
            label: 'Confirm password',
            type: 'password',
            value: confirmPassword,
            onChange: setConfirmPassword,
            autoComplete: 'new-password',
            disabled: loading || !!status,
          },
        ]}
        onSubmit={handleSubmit}
        submitLabel="Create account"
        loading={loading}
        error={error}
        status={status}
      />

      {!status && (
        <p className="auth-form__links">
          <Link to={`/login${redirect !== '/' ? `?redirect=${encodeURIComponent(redirect)}` : ''}`}>
            Back to sign in
          </Link>
        </p>
      )}

      {status && (
        <p className="auth-form__links">
          <Link to="/login">Go to sign in</Link>
        </p>
      )}
    </AuthLayout>
  );
}

export default SignupPage;
